import React, { useEffect, useState } from 'react';
import { Handshake, Loader2, Plus, Save, Trash2, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import { DashboardLayout } from '../../app/layouts/DashboardLayout';
import { ConfirmDialog } from '../../components/common/ConfirmDialog';
import { ImageUpload } from '../../components/common/ImageUpload';
import { ADMIN_NAV } from '../../constants/nav';
import {
  adminListBrokers,
  adminCreateBroker,
  adminUpdateBroker,
  adminDeleteBroker,
  Broker,
} from '../../services/api/brokers';

const inputClass = 'w-full bg-[#090D1A] border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder:text-white/30 focus:border-cyan-400 outline-none transition-colors';

export default function AdminBrokersPage() {
  const [brokers, setBrokers] = useState<Broker[]>([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState('');
  const [newUrl, setNewUrl] = useState('');
  const [newLogo, setNewLogo] = useState('');
  const [saving, setSaving] = useState(false);
  const [confirmDel, setConfirmDel] = useState<Broker | null>(null);
  const [delLoading, setDelLoading] = useState(false);

  const load = () => { setLoading(true); adminListBrokers().then(setBrokers).finally(() => setLoading(false)); };
  useEffect(load, []);

  const add = async () => {
    if (!newName.trim()) return toast.error('Nama broker wajib diisi');
    setSaving(true);
    try { await adminCreateBroker({ name: newName.trim(), referral_url: newUrl.trim(), logo_url: newLogo, is_active: true }); toast.success('Broker ditambahkan'); setNewName(''); setNewUrl(''); setNewLogo(''); load(); }
    catch (e: any) { toast.error('Gagal', { description: e.response?.data?.detail || e.message }); }
    finally { setSaving(false); }
  };

  const saveRow = async (b: Broker) => {
    try { await adminUpdateBroker(b.id, { name: b.name, referral_url: b.referral_url, logo_url: b.logo_url, is_active: b.is_active }); toast.success('Tersimpan'); }
    catch (e: any) { toast.error('Gagal', { description: e.response?.data?.detail || e.message }); }
  };

  const patch = (id: string, changes: Partial<Broker>) => setBrokers((prev) => prev.map((b) => (b.id === id ? { ...b, ...changes } : b)));

  return (
    <DashboardLayout navItems={ADMIN_NAV} title="Broker Rekanan">
      <div className="max-w-3xl" data-testid="admin-brokers-page">
        <div className="mb-6 flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-cyan-400/10 border border-cyan-400/30 flex items-center justify-center">
            <Handshake className="w-5 h-5 text-cyan-400" strokeWidth={1.5} />
          </div>
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-cyan-400">Management</p>
            <h2 className="text-2xl sm:text-3xl tracking-tight font-medium text-white">Broker Rekanan</h2>
          </div>
        </div>

        <div className="bg-white/[0.02] border border-white/10 rounded-2xl p-4 mb-6 flex flex-col sm:flex-row gap-2 items-end">
          <div className="shrink-0 w-full sm:w-auto">
            <label className="block text-xs uppercase tracking-wider text-white/50 mb-1.5">Logo</label>
            <ImageUpload value={newLogo} onUploaded={setNewLogo} label="Logo" testId="broker-new-logo" />
          </div>
          <div className="flex-1 w-full">
            <label className="block text-xs uppercase tracking-wider text-white/50 mb-1.5">Nama Broker</label>
            <input className={inputClass} data-testid="broker-new-name" value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Exness" />
          </div>
          <div className="flex-1 w-full">
            <label className="block text-xs uppercase tracking-wider text-white/50 mb-1.5">Link Referral</label>
            <input className={inputClass} data-testid="broker-new-url" value={newUrl} onChange={(e) => setNewUrl(e.target.value)} placeholder="https://..." />
          </div>
          <button data-testid="broker-add-button" onClick={add} disabled={saving} className="bg-cyan-400 text-[#050914] font-medium px-4 py-2 rounded-lg hover:bg-cyan-300 disabled:opacity-50 flex items-center gap-2 shrink-0">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" strokeWidth={2} /> : <Plus className="w-4 h-4" strokeWidth={2} />} Tambah
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 text-cyan-400 animate-spin" strokeWidth={1.5} /></div>
        ) : brokers.length === 0 ? (
          <p className="text-white/40 text-sm bg-white/[0.02] border border-white/10 rounded-xl p-6">Belum ada broker. Tambahkan di atas.</p>
        ) : (
          <div className="space-y-2">
            {brokers.map((b) => (
              <div key={b.id} data-testid="broker-row" className="bg-white/[0.02] border border-white/10 rounded-xl p-3 flex flex-col sm:flex-row gap-2 items-stretch sm:items-center">
                <div className="w-full sm:w-auto shrink-0">
                  <ImageUpload value={b.logo_url} onUploaded={(url) => patch(b.id, { logo_url: url })} label="Logo" testId={`broker-logo-${b.id}`} />
                </div>
                <input className={inputClass + ' sm:w-40'} value={b.name} onChange={(e) => patch(b.id, { name: e.target.value })} />
                <input className={inputClass + ' flex-1'} value={b.referral_url} onChange={(e) => patch(b.id, { referral_url: e.target.value })} placeholder="https://..." />
                <label className="flex items-center gap-1.5 text-xs text-white/60 cursor-pointer shrink-0">
                  <input type="checkbox" checked={b.is_active} onChange={(e) => patch(b.id, { is_active: e.target.checked })} className="accent-cyan-400 w-4 h-4" /> Aktif
                </label>
                <button data-testid="broker-save-button" onClick={() => saveRow(b)} title="Simpan" className="p-2 rounded-lg text-white/60 hover:text-cyan-400 hover:bg-white/5 transition-colors"><Save className="w-4 h-4" strokeWidth={1.6} /></button>
                <button data-testid="broker-delete-button" onClick={() => setConfirmDel(b)} title="Hapus" className="p-2 rounded-lg text-white/60 hover:text-red-400 hover:bg-white/5 transition-colors"><Trash2 className="w-4 h-4" strokeWidth={1.6} /></button>
              </div>
            ))}
          </div>
        )}
      </div>

      <ConfirmDialog
        open={!!confirmDel}
        title="Hapus broker?"
        description={confirmDel ? `Broker "${confirmDel.name}" akan dihapus.` : ''}
        loading={delLoading}
        onConfirm={async () => {
          if (!confirmDel) return;
          setDelLoading(true);
          try { await adminDeleteBroker(confirmDel.id); toast.success('Broker dihapus'); load(); }
          catch (e: any) { toast.error('Gagal', { description: e.response?.data?.detail || e.message }); }
          finally { setDelLoading(false); setConfirmDel(null); }
        }}
        onCancel={() => setConfirmDel(null)}
      />
    </DashboardLayout>
  );
}
